import * as React from "react";
import { ColDef, GridApi, RowClassParams } from "ag-grid-community";
import { dateFormatter, euroFormatterTwoDecimals, paramsEuroFormatter } from "@/utils/formatters";
import Grid, { GridButton, GridDropdownFilter, SelectedRowsActions } from "@/lib/organisms/Grid";
import { Platform } from "@/models";
import { booleanSetFilter, greekDateColumnFilter } from "@/utils/grid";

export interface ReservationRow {
    id: number;
    /**
     * Numeric apartment ID retained on the row so cross-cutting filters
     * (e.g. the shared `<ApartmentFilterBar />` predicates) can resolve a
     * row's apartment back to its `Apartment` record without re-reading
     * the source `Reservation` list. The visible `apartment` field carries
     * the human-readable name.
     */
    apartmentIdInternal?: number;
    apartment: string;
    strApartmentID: number;
    platformEnum: Platform;
    platform: string;
    reservationCode: string;
    bookingDate: Date;
    startDate: Date;
    endDate: Date;
    actualDaysOfStay: number;
    guestName: string;
    guestId: string;
    numberOfGuests: number;
    receivableIncome: number;
    netIncome: number;
    grossAdr: number;
    netAdr: number;
    vat: number;
    sustainabilityFee: number;
    temporaryStayDuty: number;
    amountPaid?: number;
    paymentDate?: Date;
    commission: number;
    cleaningFee?: number;
    cleaningCost?: number;
    hostingExpense?: number;
    epsilon?: boolean;
    uploadedToShortTermRegistry?: boolean;
    /**
     * HostHub-ingestion indicators (M9, ui-spec Screen 1). All optional on the
     * wire (`origin`, `isCancelled`, `needsRefile`); read defensively at the
     * cell-renderer boundary (`origin ?? "manual"`, etc.).
     */
    origin?: string;
    isCancelled?: boolean;
    /**
     * Present only on a cancellation the channel delivered. Its absence on a
     * cancelled row is what makes that row a void (ADR-038).
     */
    cancelledAt?: Date;
    needsRefile?: boolean;
    /**
     * The reservation no longer tracks HostHub because it was manually
     * overridden (manual-reservation-override/FR-013).
     */
    isSyncFrozen?: boolean;
}

export interface ReservationPageProps {
    reservations: ReservationRow[];
    gridDropdownFilters?: GridDropdownFilter[];
    gridButtons?: GridButton[];
    reservationActions?: SelectedRowsActions[];
    extraColumnDefinitions?: ColDef[];
    onGridReady?: (gridApi: GridApi<ReservationRow>) => void;
    getRowClass?: (params: RowClassParams<ReservationRow>) => string | string[] | undefined;
}

/**
 * Builds the reservations grid's base column definitions plus the named columns
 * the aggregation footer references by identity. Exported (rather than inlined
 * in the component) so tests can assert against the *source* column config —
 * e.g. that every `agDateColumnFilter` column also declares
 * `cellDataType: "date"` (AG Grid silently infers a data type from row values
 * when the property is omitted, so asserting on the grid-resolved def would be a
 * false-green; the source def is the real contract).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function buildReservationColumnDefinitions(): {
    columnDefinitions: ColDef[];
    amountPaidColumn: ColDef;
    grossIncomeColumn: ColDef;
    actualDaysColumn: ColDef;
} {
    const amountPaidColumn: ColDef = {
        field: "amountPaid",
        headerName: "Paid Amount",
        valueFormatter: paramsEuroFormatter,
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        cellClass: "currencyFormat",
        width: 110,
        filterValueGetter: (value) => {
            if (value.data !== undefined) {
                return Math.round(value.data.amountPaid);
            }
        },
    };
    const grossIncomeColumn: ColDef = {
        field: "receivableIncome",
        headerName: "Gross Income",
        valueFormatter: paramsEuroFormatter,
        cellDataType: "number",
        cellClass: "currencyFormat",
        filter: "agNumberColumnFilter",
        width: 110,
        filterValueGetter: (value) => {
            if (value.data !== undefined) {
                return Math.round(value.data.receivableIncome);
            }
        },
    };
    const cleaningFeeColumn: ColDef = {
        field: "cleaningFee",
        headerName: "Cleaning Fee",
        valueFormatter: paramsEuroFormatter,
        cellDataType: "number",
        cellClass: "currencyFormat",
        filter: "agNumberColumnFilter",
        width: 110,
        filterValueGetter: (value) => {
            if (value.data !== undefined) {
                return Math.round(value.data.cleaningFee);
            }
        },
    };

    const actualDaysColumn: ColDef = {
        field: "actualDaysOfStay",
        headerName: "Actual Days",
        width: 95,
        cellDataType: "number",
        filter: "agNumberColumnFilter",
    };
    const columnDefinitions: ColDef[] = [
        {
            field: "reservationCode",
            headerName: "Code",
            cellDataType: "text",
            filter: "agTextColumnFilter",
            width: 160,
        },
        {
            field: "apartment",
            headerName: "Apartment",
            cellDataType: "text",
            filter: "agTextColumnFilter",
            width: 270,
        },
        {
            field: "platform",
            headerName: "Platform",
            filter: "agSetColumnFilter",
            width: 125,
        },
        {
            field: "bookingDate",
            headerName: "Booking Date",
            valueFormatter: dateFormatter,
            cellDataType: "date",
            cellClass: "dateType",
            width: 210,
            ...greekDateColumnFilter,
        },
        {
            field: "startDate",
            headerName: "Start Date",
            valueFormatter: dateFormatter,
            cellDataType: "date",
            width: 210,
            cellClass: "dateType",
            ...greekDateColumnFilter,
        },
        {
            field: "endDate",
            headerName: "End Date",
            valueFormatter: dateFormatter,
            cellDataType: "date",
            width: 210,
            cellClass: "dateType",
            ...greekDateColumnFilter,
        },
        actualDaysColumn,
        {
            field: "guestName",
            headerName: "Guest",
            cellDataType: "text",
            filter: "agTextColumnFilter",
            width: 140,
        },
        grossIncomeColumn,
        {
            field: "numberOfGuests",
            headerName: "N. Guests",
            filter: "agSetColumnFilter",
            width: 95,
        },
        amountPaidColumn,
        {
            field: "paymentDate",
            headerName: "Payment Date",
            valueFormatter: dateFormatter,
            cellDataType: "date",
            width: 210,
            cellClass: "dateType",
            ...greekDateColumnFilter,
        },
        {
            field: "commission",
            headerName: "Commission",
            valueFormatter: paramsEuroFormatter,
            cellDataType: "number",
            cellClass: "currencyFormat",
            filter: "agNumberColumnFilter",
            width: 130,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.commission);
                }
            },
        },
        cleaningFeeColumn,
        // Boolean checkbox cell. Without an explicit filter it inherits
        // `filter: true` from defaultColDef, which resolves to a Set Filter
        // offering raw True/False rather than the Yes/No a user can read.
        { field: "epsilon", headerName: "ERP Done", width: 90, ...booleanSetFilter("Yes", "No") },
        {
            field: "netIncome",
            headerName: "Net Income",
            valueFormatter: paramsEuroFormatter,
            width: 110,
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.netIncome);
                }
            },
        },
        {
            field: "grossAdr",
            headerName: "Gross Adr",
            valueFormatter: paramsEuroFormatter,
            width: 100,
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.grossAdr);
                }
            },
        },
        {
            field: "netAdr",
            headerName: "Net Adr",
            valueFormatter: paramsEuroFormatter,
            width: 100,
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.netAdr);
                }
            },
        },
        {
            field: "vat",
            headerName: "VAT",
            valueFormatter: paramsEuroFormatter,
            width: 110,
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.vat);
                }
            },
        },
        {
            field: "sustainabilityFee",
            headerName: "Sustainability Fee",
            valueFormatter: paramsEuroFormatter,
            width: 140,
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.sustainabilityFee);
                }
            },
        },
        {
            field: "temporaryStayDuty",
            headerName: "Temporary Stay Duty",
            valueFormatter: paramsEuroFormatter,
            width: 140,
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.temporaryStayDuty);
                }
            },
        },
    ];

    return { columnDefinitions, amountPaidColumn, grossIncomeColumn, actualDaysColumn };
}

/**
 * Shared empty default for `extraColumnDefinitions`. A literal `= []` default
 * allocates a NEW array on every render for callers that omit the prop, which
 * would defeat the memo below.
 */
const NO_EXTRA_COLUMNS: ColDef[] = [];

export const ReservationsPage: React.FC<ReservationPageProps> = ({
    gridButtons,
    gridDropdownFilters,
    reservationActions,
    onGridReady,
    reservations,
    extraColumnDefinitions = NO_EXTRA_COLUMNS,
    getRowClass,
}: ReservationPageProps) => {
    const gridName = "Reservations";

    // The column definitions are static, so build them once. A fresh `columnDefs`
    // identity makes AG Grid re-process the columns, and where a saved view has
    // reordered them it reports a `gridOptionsChanged` move — which Grid reads as
    // the user having edited their view, lighting up Save after any re-render.
    const { columnDefinitions, amountPaidColumn, grossIncomeColumn, actualDaysColumn } = React.useMemo(
        () => buildReservationColumnDefinitions(),
        [],
    );

    const columnDefinitionsWithExtra = React.useMemo(
        () => [...columnDefinitions, ...extraColumnDefinitions],
        [columnDefinitions, extraColumnDefinitions],
    );

    let optionalProps = {};
    if (gridButtons) {
        optionalProps = { buttons: gridButtons };
    }
    if (reservationActions) {
        optionalProps = { ...optionalProps, actions: reservationActions };
    }

    return (
        <Grid<ReservationRow>
            getRowId={(params) => params.data.id.toString()}
            columnDefs={columnDefinitionsWithExtra}
            onGridReady={onGridReady}
            dropdownFilters={gridDropdownFilters}
            gridName={gridName}
            getRowClass={getRowClass}
            initialRowData={reservations}
            {...optionalProps}
            columnsToAggregate={[
                { colDef: actualDaysColumn, formatter: (x) => x },
                { colDef: grossIncomeColumn, formatter: euroFormatterTwoDecimals },
                { colDef: amountPaidColumn, formatter: euroFormatterTwoDecimals },
            ]}
        />
    );
};

export default ReservationsPage;
